import { useFormContext, UseFormReturn } from 'react-hook-form';
import { 
  FinalFormProfile, 
  Guideline, 
  APPROVED_NUTRIENT_RELATED_PHRASES,
  BRAND_NAME_FORMATTING,
  APPROVED_SUBSTITUTIONS,
  AVOID_CROSS_CATEGORY_COMPARISONS,
  AVOID_SPECIFIC_CLAIM_LANGUAGE,
  APPROVED_ON_PACKAGE_LANGUAGE,
  APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS,
  APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT,
  AVOID_RESTRICTED_TEXT_FOR_CLAIMS
} from '../types/profileTypes';
import { ModalFormSchema, ModalFormValuesTypes } from '../schemas/modalFormSchema';
import { z } from 'zod';


/**
 * Constructs a nutrientStatement from the individual fields
 * @param amount The amount value
 * @param unit The unit value
 * @param nutrient The nutrient value
 * @returns The constructed nutrientStatement
 */
const constructNutrientStatement = (amount: string, unit: string, nutrient: string): string => {
  return `${amount} ${unit} of ${nutrient}`;
};

/**
 * Updates the main form with a guideline at the specified index
 * @param mainForm The main form context
 * @param index The index to update
 * @param guideline The guideline to set at the index
 */
export const updateMainFormAtIndex = (
  mainForm: UseFormReturn<FinalFormProfile>,
  index: number, 
  guideline: ModalFormValuesTypes
) => {
  // Create a copy of the guideline to avoid modifying the original
  const updatedGuideline = { ...guideline } as Guideline;
  
  // Handle special case for nutrient statements
  if (guideline.type === APPROVED_NUTRIENT_RELATED_PHRASES && 
      'amount' in guideline.parameters && 
      'unit' in guideline.parameters && 
      'nutrient' in guideline.parameters) {
    const amount = guideline.parameters.amount as string;
    const unit = guideline.parameters.unit as string;
    const nutrient = guideline.parameters.nutrient as string;
    
    // Construct the nutrientStatement
    if (amount && unit && nutrient) {
      const nutrientStatement = constructNutrientStatement(amount, unit, nutrient);
      
      // Update the guideline with the constructed nutrientStatement
      updatedGuideline.parameters = {
        ...updatedGuideline.parameters,
        nutrientStatement
      };
    }
  }
  
  const currentGuidelines = mainForm.getValues('settings.guidelines') || [];
  
  // Create a new array with the updated guideline at the specified index
  const updatedGuidelines: Guideline[] = [...currentGuidelines];
  updatedGuidelines[index] = updatedGuideline;
  
  // Update the main form with the new guidelines array
  mainForm.setValue('settings.guidelines', updatedGuidelines, {
    shouldValidate: true,
    shouldDirty: true,
    shouldTouch: true
  });
};

/**
 * Updates the modal form with the provided guideline
 * @param guideline The guideline to load into the modal form
 * @param modalForm The form context of the modal
 */
export const updateModalForm = (
  guideline: Guideline, 
  modalForm: UseFormReturn<ModalFormValuesTypes>
) => {
  if (!guideline || !modalForm) return;
  
  // Set the type field
  modalForm.setValue('type', guideline.type);
  
  // Handle special case for nutrient statements
  if (guideline.type === APPROVED_NUTRIENT_RELATED_PHRASES && 
      'nutrientStatement' in guideline.parameters && 
      guideline.parameters.nutrientStatement) {
    const nutrientStatement = guideline.parameters.nutrientStatement;
    
    // Regular expression to match the pattern: "amount unit of nutrient"
    const regex = /^(\d+(?:\.\d+)?)\s+([^(]+(?:\([^)]+\))?)\s+of\s+(.+)$/;
    const match = nutrientStatement.match(regex);
    
    if (match) {
      const [, amount, unit, nutrient] = match;
      
      // Set the individual fields in the form
      modalForm.setValue('parameters.nutrient', nutrient.trim());
      modalForm.setValue('parameters.amount', amount);
      modalForm.setValue('parameters.unit', unit.trim());
      
      // Set all parameters from the guideline
      if (guideline.parameters) {
        Object.entries(guideline.parameters).forEach(([key, value]) => {
          if (key !== 'nutrient') {
            modalForm.setValue(`parameters.${key}`, value);
          }
        });
      }
    }
  } else {
    // For all other guideline types, set the parameters directly
    if (guideline.parameters) {
      Object.entries(guideline.parameters).forEach(([key, value]) => {
        modalForm.setValue(`parameters.${key}`, value);
      });
    }
  }
};

/**
 * Removes a guideline at the specified index
 * @param mainForm The main form context
 * @param index The index to remove
 */
export const removeItemAtIndex = (
  mainForm: UseFormReturn<FinalFormProfile>,
  index: number
) => {
  const currentGuidelines = mainForm.getValues('settings.guidelines') || [];
  
  // Create a new array without the guideline at the specified index
  const updatedGuidelines: Guideline[] = [...currentGuidelines];
  updatedGuidelines.splice(index, 1);
  
  // Update the main form with the new guidelines array
  mainForm.setValue('settings.guidelines', updatedGuidelines, {
    shouldValidate: true,
    shouldDirty: true,
    shouldTouch: true
  });
};

/**
 * Hook for connecting the main form and modal form
 */
export const useFormConnection = () => {
  const mainForm = useFormContext<FinalFormProfile>();
  
  return {
    updateMainFormAtIndex: (index: number, guideline: ModalFormValuesTypes) => 
      updateMainFormAtIndex(mainForm, index, guideline),
    updateModalForm: (guideline: Guideline, modalForm: UseFormReturn<ModalFormValuesTypes>) => 
      updateModalForm(guideline, modalForm),
    removeItemAtIndex: (index: number) => 
      removeItemAtIndex(mainForm, index)
  };
}; 